import { supabase } from '../lib/supabaseClient'
import type { Noticia, NoticiaDB, NoticiaFormData, NoticiaFiltros, NoticiaResponse, NoticiaStatus } from '../types/noticias'

// Helper function to determine status from date
function getStatus(dateStr: string): NoticiaStatus {
  const date = new Date(dateStr)
  const now = new Date()
  if (date.getUTCFullYear() >= 3000) return 'draft'
  if (date > now) return 'scheduled'
  return 'published'
}

// Helper to generate slug
function generateSlug(text: string): string {
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
}

// Helper function to map database row to frontend Noticia type
function mapDBToNoticia(dbRow: NoticiaDB): Noticia {
  return {
    id: dbRow.id,
    titulo: dbRow.title,
    conteudo: dbRow.content || '',
    resumo: dbRow.excerpt || undefined,
    autor: dbRow.created_by || 'Desconhecido',
    data_criacao: dbRow.created_at,
    data_atualizacao: dbRow.updated_at,
    destaque: dbRow.featured || false,
    imagem_url: dbRow.image || undefined,
    status: getStatus(dbRow.date),
    data_publicacao: dbRow.date,
    slug: dbRow.slug
  }
}

export const noticiasService = {
  // Listar notícias com filtros e paginação
  async listarNoticias(
    filtros: NoticiaFiltros = { status: 'published' }, // Default safe
    pagina: number = 1,
    limite: number = 10
  ): Promise<{ data: NoticiaResponse | null; error: string | null }> {
    try {
      let query = supabase
        .from('noticias')
        .select('*', { count: 'exact' })

      // Aplicar filtros
      if (filtros.busca) {
        query = query.or(`title.ilike.%${filtros.busca}%,content.ilike.%${filtros.busca}%`)
      }

      if (filtros.destaque !== undefined) {
        query = query.eq('featured', filtros.destaque)
      }

      // Filtro de Status (baseado na data)
      const nowISO = new Date().toISOString()

      if (filtros.status === 'published') {
        query = query.lte('date', nowISO)
      } else if (filtros.status === 'draft') {
        query = query.gte('date', '3000-01-01')
      } else if (filtros.status === 'scheduled') {
        query = query.gt('date', nowISO).lt('date', '3000-01-01')
      }
      // If status is 'all', apply no date filter (shows drafts too)

      // Ordenar por data de criação (mais recentes primeiro)
      query = query.order('created_at', { ascending: false })

      // Aplicar paginação
      const inicio = (pagina - 1) * limite
      const fim = inicio + limite - 1

      const { data, error, count } = await query.range(inicio, fim)

      if (error) throw error

      const total = count || 0
      const totalPaginas = Math.ceil(total / limite)

      // Mapear dados do banco para o tipo Noticia
      const noticias: Noticia[] = (data || []).map(item => mapDBToNoticia(item as NoticiaDB))

      return {
        data: {
          noticias,
          total,
          pagina,
          total_paginas: totalPaginas
        },
        error: null
      }
    } catch (error: any) {
      console.error('Erro ao listar notícias:', error)
      return { data: null, error: error.message || 'Erro interno do servidor' }
    }
  },

  // Buscar notícia por ID
  async buscarNoticiaPorId(id: string): Promise<{ data: Noticia | null; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('noticias')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error

      if (!data) {
        return { data: null, error: 'Notícia não encontrada' }
      }

      const noticia = mapDBToNoticia(data as NoticiaDB)
      return { data: noticia, error: null }
    } catch (error: any) {
      console.error('Erro ao buscar notícia:', error)
      return { data: null, error: error.message || 'Erro interno do servidor' }
    }
  },

  // Criar nova notícia
  async criarNoticia(
    noticiaData: NoticiaFormData,
    autorId: string
  ): Promise<{ data: Noticia | null; error: string | null }> {
    try {
      // Determinar data baseada no status
      let dateValue = new Date().toISOString()

      if (noticiaData.status === 'draft') {
        dateValue = '3000-01-01T00:00:00Z'
      } else if (noticiaData.data_agendamento) {
        dateValue = new Date(noticiaData.data_agendamento).toISOString()
      } else {
        // Published: now default
        dateValue = new Date().toISOString()
      }

      const dbData = {
        title: noticiaData.titulo,
        content: noticiaData.conteudo,
        excerpt: noticiaData.resumo || null,
        image: noticiaData.imagem_url || null,
        featured: noticiaData.destaque,
        created_by: autorId,
        date: dateValue,
        slug: generateSlug(noticiaData.titulo)
      }

      const { data, error } = await (supabase
        .from('noticias') as any)
        .insert(dbData)
        .select()
        .single()

      if (error) throw error

      const noticia = mapDBToNoticia(data as NoticiaDB)
      return { data: noticia, error: null }
    } catch (error: any) {
      console.error('Erro ao criar notícia:', error)
      return { data: null, error: error.message || 'Erro interno do servidor' }
    }
  },

  // Atualizar notícia
  async atualizarNoticia(
    id: string,
    noticiaData: Partial<NoticiaFormData>
  ): Promise<{ data: Noticia | null; error: string | null }> {
    try {
      const dbData: any = {}

      if (noticiaData.titulo !== undefined) {
        dbData.title = noticiaData.titulo
        dbData.slug = generateSlug(noticiaData.titulo)
      }
      if (noticiaData.conteudo !== undefined) dbData.content = noticiaData.conteudo
      if (noticiaData.resumo !== undefined) dbData.excerpt = noticiaData.resumo || null
      if (noticiaData.imagem_url !== undefined) dbData.image = noticiaData.imagem_url || null
      if (noticiaData.destaque !== undefined) dbData.featured = noticiaData.destaque

      // Update date logic
      if (noticiaData.status) {
        // Universal date update logic
        if (noticiaData.status === 'draft') {
          dbData.date = '3000-01-01T00:00:00Z'
        } else if (noticiaData.data_agendamento) {
          dbData.date = new Date(noticiaData.data_agendamento).toISOString()
        } else if (noticiaData.status === 'published') {
          // If changing TO published without date, set NOW if it was previously draft?
          // Complex. But simply: if no date provided, and we are updating status to published?
          // Best approach: If status provided and is published, fetch current.
          // If current was draft, set NOW. If current was normal, keep it.
          const { data: current } = await supabase.from('noticias').select('date').eq('id', id).single()
          const currentDate = new Date((current as any)?.date || '')
          if (currentDate.getUTCFullYear() >= 3000) {
            dbData.date = new Date().toISOString()
          }
          // Else keep existing date.
        }
      }

      const { data, error } = await (supabase
        .from('noticias') as any)
        .update(dbData)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error

      const noticia = mapDBToNoticia(data as NoticiaDB)
      return { data: noticia, error: null }
    } catch (error: any) {
      console.error('Erro ao atualizar notícia:', error)
      return { data: null, error: error.message || 'Erro interno do servidor' }
    }
  },

  // Excluir notícia
  async excluirNoticia(id: string): Promise<{ error: string | null }> {
    try {
      const { error, count } = await supabase
        .from('noticias')
        .delete({ count: 'exact' })
        .eq('id', id)

      if (error) throw error

      if (count === 0) {
        return { error: 'Não foi possível excluir. Verifique se você tem permissão ou se a notícia existe.' }
      }

      return { error: null }
    } catch (error: any) {
      console.error('Erro ao excluir notícia:', error)
      return { error: error.message || 'Erro interno do servidor' }
    }
  },

  // Alternar status de destaque
  async alternarDestaque(id: string): Promise<{ data: Noticia | null; error: string | null }> {
    try {
      // Primeiro buscar o estado atual
      const { data: current, error: fetchError } = await supabase
        .from('noticias')
        .select('featured, date') // Fetch date too for mapping
        .eq('id', id)
        .single()

      if (fetchError) throw fetchError

      const { data, error } = await (supabase
        .from('noticias') as any)
        .update({ featured: !(current as any).featured })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error

      const noticia = mapDBToNoticia(data as NoticiaDB)
      return { data: noticia, error: null }
    } catch (error: any) {
      console.error('Erro ao alternar destaque:', error)
      return { data: null, error: error.message || 'Erro interno do servidor' }
    }
  },

  async uploadImagem(file: File): Promise<{ url: string | null; error: string | null }> {
    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`
      const filePath = `${fileName}`

      const { error: uploadError } = await supabase.storage
        .from('noticias')
        .upload(filePath, file)

      if (uploadError) {
        return { url: null, error: uploadError.message }
      }

      const { data } = supabase.storage.from('noticias').getPublicUrl(filePath)

      return { url: data.publicUrl, error: null }
    } catch (error: any) {
      return { url: null, error: error.message || 'Erro ao fazer upload da imagem' }
    }
  }
}